import React, { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Calculate as CalculatorIcon,
  TrendingUp as UnitCostIcon,
  Public as CommerceIcon,
  Analytics as ScenarioIcon,
  Description as IeepaRefundIcon,
} from "@mui/icons-material";
import { SelectClientBanner } from "@/components";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { layoutDimensions } from "@/utils/responsive";
import { tabSx } from "@/utils/customs-helpers";
import { tokens } from "@/tokens";
import { TOOLS_TABS, TOOLS_TAB_KEYS } from "@/constants";
import { useFilteredTabs } from "@/hooks/useFilteredTabs";

const TAB_ICON_MAP: Record<string, React.ReactElement> = {
  "duty-calculator": <CalculatorIcon sx={{ fontSize: 18 }} />,
  "unit-cost-analysis": <UnitCostIcon sx={{ fontSize: 18 }} />,
  "commerce-data": <CommerceIcon sx={{ fontSize: 18 }} />,
  "scenario-modeling": <ScenarioIcon sx={{ fontSize: 18 }} />,
  "ieepa-refund-cape": <IeepaRefundIcon sx={{ fontSize: 18 }} />,
};

const Tools: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const location = useLocation();
  const navigate = useNavigate();
  const filteredTabs = useFilteredTabs(TOOLS_TABS, TOOLS_TAB_KEYS, "TOOLS");

  const activeTabSegment = useMemo(
    () => location.pathname.split("/")[2] || "",
    [location.pathname]
  );

  const activeTab = useMemo(() => {
    const idx = filteredTabs.findIndex((tab) => tab.link === activeTabSegment);
    return idx === -1 ? 0 : idx;
  }, [activeTabSegment, filteredTabs]);

  useEffect(() => {
    const found = filteredTabs.some((tab) => tab.link === activeTabSegment);
    if (!found && filteredTabs.length > 0) {
      navigate(`/tools/${filteredTabs[0].link}`, { replace: true });
    }
  }, [activeTabSegment, filteredTabs, navigate]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = filteredTabs[newValue];
    if (!tab) return;
    navigate(`/tools/${tab.link}`);
  };

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return <SelectClientBanner />;
  }

  return (
    <Stack
      spacing={0}
      sx={{
        margin: {
          xs: `calc(-1 * ${layoutDimensions.layoutPaddingSmall}) calc(-1 * ${layoutDimensions.layoutPaddingMedium})`,
          md: `calc(-1 * ${layoutDimensions.layoutPaddingMedium}) calc(-1 * ${layoutDimensions.layoutPaddingLarge})`,
        },
        bgcolor: tokens.bg.muted,
        width: {
          xs: `calc(100% + 2 * ${layoutDimensions.layoutPaddingMedium})`,
          md: `calc(100% + 2 * ${layoutDimensions.layoutPaddingLarge})`,
        },
        height: {
          xs: `calc(100vh - ${layoutDimensions.layoutPaddingSmall} - ${layoutDimensions.layoutPaddingSmall})`,
          md: `calc(100vh - ${layoutDimensions.layoutPaddingMedium} - ${layoutDimensions.layoutPaddingMedium})`,
        },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Main Tabs - Edge to edge */}
      <Box sx={{
        borderBottom: `1px solid ${tokens.border.default}`,
        margin: 0,
        padding: 0,
        width: '100%',
        bgcolor: tokens.bg.muted,
        flexShrink: 0,
      }}>
        <Stack direction="row" alignItems="center" sx={{ minHeight: 48, px: { xs: 1.5, md: 3 }, margin: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              maxWidth: '100%',
              '& .MuiTabs-indicator': {
                backgroundColor: tokens.text.primary,
                height: 2,
                bottom: 0,
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            {filteredTabs.map((tab) => (
              <Tab
                key={tab.link}
                label={tab.text}
                icon={TAB_ICON_MAP[tab.link]}
                iconPosition="start"
                sx={{ ...tabSx, gap: 0.75 }}
              />
            ))}
          </Tabs>
        </Stack>
      </Box>

      {/* Tab Content - fills remaining height */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        boxSizing: 'border-box',
        bgcolor: tokens.bg.muted,
        p: 3,
      }}>
        <Outlet />
      </Box>

    </Stack>
  );
};

export default Tools;
